
import React from 'react';
import { User, Session } from '@/types/auth';
import { backendClient } from '@/lib/backendClient';
import { handleSecureError, secureLog } from '@/utils/secureErrorHandler';
import { getAuthRedirectUrl } from '@/utils/canonicalUrl';
import { processReferralSignup, getReferralCodeFromUrl, clearReferralCodeFromUrl } from '@/utils/referralUtils';
import { authService } from '@/utils/authService';
import { clearWebSocketTokenCache } from '@/hooks/useWebSocket';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName?: string, referralCodeId?: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  deleteAccount: () => Promise<{ error: any }>;
  setAuthState: (userData: any) => void;
}

const AuthContext = React.createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth state cleanup utility
const cleanupAuthState = () => {
  try {
    // Remove standard auth tokens
    localStorage.removeItem('backendClient.auth.token');
    
    // Remove all Supabase auth keys from localStorage
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('backendClient.auth.') || key.includes('sb-')) {
        localStorage.removeItem(key);
      }
    });
    
    // Remove from sessionStorage if in use
    if (typeof sessionStorage !== 'undefined') {
      Object.keys(sessionStorage).forEach((key) => {
        if (key.startsWith('backendClient.auth.') || key.includes('sb-')) {
          sessionStorage.removeItem(key);
        }
      });
    }
    
    secureLog('info', 'Auth state cleaned up successfully');
  } catch (error) {
    secureLog('error', 'Failed to cleanup auth state', { error });
  }
};

import { logger } from '@/utils/logger';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = React.useState<User | null>(null);
  const [session, setSession] = React.useState<Session | null>(null);
  const [loading, setLoading] = React.useState(true);
  const authOperationInProgress = React.useRef(false);

  React.useEffect(() => {
    const initAuth = async () => {
      const isBackendMode = import.meta.env.VITE_USE_BACKEND === 'true';

      if (isBackendMode) {
        // Check if user is authenticated via cookies
        const isAuthenticated = localStorage.getItem('is_authenticated');

        if (isAuthenticated === 'true') {
          try {
            // Verify session is still valid using cookies
            const response = await fetch(`${import.meta.env.VITE_BACKEND_URL}/auth/me`, {
              credentials: 'include' // Include cookies
            });

            if (response.ok) {
              const userData = await response.json();
              setUser(userData);
              setSession({
                access_token: 'cookie-based',
                user: userData
              } as any);
            } else {
              // Session invalid, clear it
              localStorage.removeItem('is_authenticated');
              localStorage.removeItem('user_data');
            }
          } catch (error) {
            console.error('Session validation failed:', error);
            localStorage.removeItem('is_authenticated');
            localStorage.removeItem('user_data');
          }
        }
        setLoading(false);
      } else {
        // Backend auth handling
        const { data: { session } } = await backendClient.auth.getSession();
        if (session) {
          setSession(session);
          setUser(session.user);
        }
        setLoading(false);
      }
    };

    initAuth();

    // Set up backend auth listener
    if (import.meta.env.VITE_USE_BACKEND !== 'true') {
      const { data: { subscription } } = backendClient.auth.onAuthStateChange(
        (event, session) => {
          setSession(session);
          setUser(session?.user ?? null);
          setLoading(false);
        }
      );

      return () => {
        subscription.unsubscribe();
      };
    }
  }, []);

  const ensureProfile = async (user: User) => {
    try {
      // Check if profile exists
      const { data: existingProfile, error: checkError } = await backendClient
        .from('profiles')
        .select('id')
        .eq('id', user.id)
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        secureLog('error', 'Error checking profile', { error: checkError });
        return;
      }

      // Create profile if it doesn't exist
      if (!existingProfile) {
        const profileData = {
          id: user.id,
          email: user.email,
          full_name: user.user_metadata?.full_name || '',
          first_name: user.user_metadata?.first_name || '',
          last_name: user.user_metadata?.last_name || '',
          name_usage_preference: user.user_metadata?.name_usage_preference || 'first',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };

        const { error: createError } = await backendClient
          .from('profiles')
          .insert(profileData);

        if (createError) {
          secureLog('error', 'Error creating profile', { error: createError });
        } else {
          secureLog('info', 'Profile created successfully');
        }
      }
    } catch (error) {
      secureLog('error', 'Error in ensureProfile', { error });
    }
  };

  const signUp = async (email: string, password: string, fullName?: string, referralCodeId?: string) => {
    try {
      secureLog('info', 'Attempting user sign up');

      // Parse full name into first and last
      const nameParts = fullName?.split(' ') || [];
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const { data, error } = await authService.auth.signUp(email, password, {
        full_name: fullName || '',
        first_name: firstName,
        last_name: lastName,
        referral_code_id: referralCodeId
      });

      if (error) {
        const secureError = handleSecureError(error, 'auth_sign_up');
        return { error: secureError };
      }

      return { error: null };
    } catch (error) {
      const secureError = handleSecureError(error, 'auth_sign_up');
      return { error: secureError };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      secureLog('info', 'Attempting user sign in');
      const { data, error } = await authService.auth.signIn(email, password);
      
      if (error) {
        const secureError = handleSecureError(error, 'auth_sign_in');
        return { error: secureError };
      }
      
      // For backend auth, manually update state
      if (import.meta.env.VITE_USE_BACKEND === 'true' && data) {
        setUser(data.user);
        setSession(data.session);
      }
      
      return { error: null };
    } catch (error) {
      const secureError = handleSecureError(error, 'auth_sign_in');
      return { error: secureError };
    }
  };

  const signOut = async () => {
    try {
      secureLog('info', 'Signing out user');
      setUser(null);
      setSession(null);
      cleanupAuthState();
      clearWebSocketTokenCache(); // Clear WebSocket token cache on logout

      await authService.auth.signOut();

      window.location.href = '/auth';
    } catch (error) {
      const secureError = handleSecureError(error, 'auth_sign_out');
      secureLog('error', 'Error during sign out', { error: secureError.message });
      clearWebSocketTokenCache(); // Clear even on error
      window.location.href = '/auth';
    }
  };

  const deleteAccount = async () => {
    try {
      if (!user) {
        return { error: { message: 'No user logged in' } };
      }

      secureLog('info', 'Attempting to delete user account');

      // Use the auth service to delete account via backend API
      const { error } = await authService.auth.deleteAccount();

      if (error) {
        const secureError = handleSecureError(error, 'auth_delete_account');
        return { error: secureError };
      }

      // Clean up auth state
      cleanupAuthState();
      setUser(null);
      setSession(null);

      // Redirect to landing page
      window.location.href = '/';

      return { error: null };
    } catch (error) {
      const secureError = handleSecureError(error, 'auth_delete_account');
      return { error: secureError };
    }
  };

  const setAuthState = (userData: any) => {
    setUser(userData);
    setSession({
      access_token: 'cookie-based',
      user: userData
    } as any);
    setLoading(false);
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    deleteAccount,
    setAuthState,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
